import { auth } from "../firebase";
import { ApiError } from "./apiError";
import type { ApiErrorResponse, ApiSuccessResponse } from "../types/auth.types"

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "https://api.shoof.app/api/v1"
).replace(/\/$/, "");

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  skipAuth?: boolean;
};

function isFormData(value: unknown): value is FormData {
  return typeof FormData !== "undefined" && value instanceof FormData;
}

async function getIdToken(forceRefresh = false): Promise<string | null> {
  const currentUser = auth.currentUser;
  return currentUser ? currentUser.getIdToken(forceRefresh) : null;
}

async function parseResponseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  }

  const text = await response.text();
  return text || null;
}

async function executeRequest<T>(
  path: string,
  options: RequestOptions,
  forceTokenRefresh = false
): Promise<T> {
  const headers = new Headers(options.headers);
  const token = options.skipAuth ? null : await getIdToken(forceTokenRefresh);

  if (token) headers.set("Authorization", `Bearer ${token}`);

  let requestBody: BodyInit | undefined;
  if (options.body !== undefined && options.body !== null) {
    if (isFormData(options.body) || typeof options.body === "string" || options.body instanceof Blob) {
      requestBody = options.body as BodyInit;
    } else {
      headers.set("Content-Type", "application/json");
      requestBody = JSON.stringify(options.body);
    }
  }

  const response = await fetch(`${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`, {
    ...options,
    headers,
    body: requestBody,
  });

  if (response.status === 401 && !forceTokenRefresh && !options.skipAuth && auth.currentUser) {
    return executeRequest<T>(path, options, true);
  }

  const payload = await parseResponseBody(response);

  if (!response.ok) {
    const apiError = payload as ApiErrorResponse | null;
    throw new ApiError(
      apiError?.error?.message || `Request failed with status ${response.status}`,
      response.status,
      apiError?.error?.code || "HTTP_ERROR",
      payload
    );
  }

  const envelope = payload as ApiSuccessResponse<T> | T;
  if (envelope && typeof envelope === "object" && "success" in envelope) {
    return (envelope as ApiSuccessResponse<T>).data;
  }

  return envelope as T;
}

async function executeBlobRequest(
  path: string,
  options: RequestOptions = {},
  forceTokenRefresh = false,
): Promise<Blob> {
  const headers =
    new Headers(
      options.headers,
    );

  const token =
    options.skipAuth
      ? null
      : await getIdToken(
          forceTokenRefresh,
        );

  if (token) {
    headers.set(
      "Authorization",
      `Bearer ${token}`,
    );
  }


  let requestBody:
    BodyInit | undefined;


  if (
    options.body !== undefined &&
    options.body !== null
  ) {
    if (
      isFormData(
        options.body,
      ) ||
      typeof options.body ===
        "string" ||
      options.body instanceof
        Blob
    ) {
      requestBody =
        options.body as BodyInit;
    } else {
      headers.set(
        "Content-Type",
        "application/json",
      );

      requestBody =
        JSON.stringify(
          options.body,
        );
    }
  }


  const response =
    await fetch(
      `${API_BASE_URL}${
        path.startsWith("/")
          ? path
          : `/${path}`
      }`,
      {
        ...options,

        headers,

        body:
          requestBody,
      },
    );


  if (
    response.status === 401 &&
    !forceTokenRefresh &&
    !options.skipAuth &&
    auth.currentUser
  ) {
    return executeBlobRequest(
      path,
      options,
      true,
    );
  }


  if (!response.ok) {
    const payload =
      await parseResponseBody(
        response,
      );


    const apiError =
      payload as
        ApiErrorResponse |
        null;


    throw new ApiError(
      apiError?.error?.message ||
        `Request failed with status ${response.status}`,

      response.status,

      apiError?.error?.code ||
        "HTTP_ERROR",

      payload,
    );
  }


  return response.blob();
}

export const apiClient = {
  get<T>(path: string, options: RequestOptions = {}) {
    return executeRequest<T>(path, {...options, method: "GET"});
  },
  post<T>(path: string, body?: unknown, options: RequestOptions = {}) {
    return executeRequest<T>(path, {...options, method: "POST", body});
  },
  patch<T>(path: string, body?: unknown, options: RequestOptions = {}) {
    return executeRequest<T>(path, {...options, method: "PATCH", body});
  },
  delete<T>(path: string, options: RequestOptions = {}) {
    return executeRequest<T>(path, {...options, method: "DELETE"});
  },

  getBlob(
  path: string,
  options:
    RequestOptions = {},
) {
  return executeBlobRequest(
    path,
    {
      ...options,
      method: "GET",
    },
  );
},
};
